function firstValue(...values) {
  const hit = values.find(value => value !== undefined && value !== null && value !== '')
  return hit === undefined ? '' : hit
}

const ENVELOPE_KEYS = ['content', 'payload', 'extra', 'page', 'data', 'body']
const DIAGNOSTIC_ENVELOPE_KEYS = ['diagnostics', 'meta', 'detail', 'details', 'error', 'errors']
const DEEP_DIAGNOSTIC_ENVELOPE_KEYS = ['diagnostics', 'error', 'errors']
const MAX_ENVELOPE_DEPTH = 8
const MARKER_KEYS = ['status', 'state', 'result', 'code', 'errorCode', 'launchStatus', 'forwardStatus']
const NEED_LOGIN_FLAG_KEYS = [
  'needLogin',
  'need_login',
  'loginRequired',
  'login_required',
  'authRequired',
  'auth_required'
]
const BLOCKING_FLAG_KEYS = [
  ...NEED_LOGIN_FLAG_KEYS,
  'readOnly',
  'readOnlyQuery',
  'readOnlyAction',
  'readOnlyMode',
  'readonly',
  'read_only',
  'read_only_query',
  'read_only_action',
  'read_only_mode',
  'mock',
  'mockMode',
  'mock_mode',
  'fallback',
  'fallbackOnly',
  'fallback_only',
  'mockFallback',
  'mock_fallback',
  'unimplemented',
  'notImplemented',
  'not_implemented',
  'stub',
  'failed',
  'fail',
  'failure',
  'notSupported',
  'not_supported',
  'notConnected',
  'not_connected',
  'unavailable',
  'readFailure',
  'read_failure',
  'readFailed',
  'read_failed',
  'readUnavailable',
  'read_unavailable',
  'technicalMarker',
  'technical_marker',
  'technicalError',
  'technical_error',
  'technicalException',
  'technical_exception',
  'readBlocked',
  'read_blocked',
  'unsupported',
  'queryOnly',
  'query_only',
  'onlyQuery',
  'only_query',
  'blocked',
  'block',
  'routeUnavailable',
  'route_unavailable',
  'backendBlocked',
  'backend_blocked',
  'backendFailure',
  'backend_failure',
  'failClosed',
  'fail_closed',
  'paymentBackendBlocked',
  'payment_backend_blocked',
  'paymentStatusUnconfirmed',
  'payment_status_unconfirmed',
  'falseProof',
  'false_proof',
  'actionBlocked',
  'action_blocked',
  'writeBlock',
  'write_block',
  'writeBlocked',
  'write_blocked',
  'dataUnavailable',
  'data_unavailable'
]
const READ_SAFE_FLAG_KEYS = [
  'readOnly',
  'readOnlyQuery',
  'readonly',
  'read_only',
  'read_only_query',
  'writeBlocked',
  'write_blocked'
]
const MESSAGE_KEYS = ['msg', 'message', 'errorMsg', 'errorMessage', 'reason', 'desc', 'description']
const PAYMENT_STATUS_KEYS = ['paymentStatus', 'payment_status']
const DEFAULT_LOTTERY_FAILURE_MESSAGE = 'This feature is temporarily unavailable. Please try again later.'
const PENDING_WRITE_TOKENS = [
  'pending',
  'wait',
  'waiting',
  'processing',
  'created',
  'accepted',
  'queued',
  'in_progress',
  'in-progress',
  'in progress'
]
const EXPLICIT_PROOF_KEYS = [
  'rewardProof',
  'reward_proof',
  'serverProof',
  'server_proof',
  'affectedProof',
  'affected_proof',
  'affectedRowsProof',
  'affected_rows_proof',
  'writeProof',
  'write_proof',
  'paymentUrlProof',
  'payment_url_proof',
  'lotteryProof',
  'lottery_proof',
  'betProof',
  'bet_proof',
  'orderProof',
  'order_proof',
  'cancelProof',
  'cancel_proof',
  'refundProof',
  'refund_proof'
]
const LOTTERY_DOMAIN_PROOF_KEYS = [
  'lotteryProof',
  'lottery_proof',
  'betProof',
  'bet_proof',
  'orderProof',
  'order_proof',
  'cancelProof',
  'cancel_proof',
  'refundProof',
  'refund_proof'
]
const GENERIC_LOTTERY_WRITE_PROOF_KEYS = [
  'serverProof',
  'server_proof',
  'affectedProof',
  'affected_proof',
  'affectedRowsProof',
  'affected_rows_proof',
  'writeProof',
  'write_proof'
]
const WRITE_COUNT_PROOF_KEYS = [
  'affected',
  'affectedRows',
  'affected_rows',
  'rowCount',
  'row_count'
]
const PROOF_KEYS = EXPLICIT_PROOF_KEYS.concat(WRITE_COUNT_PROOF_KEYS)
const FALSEY_PROOF_TOKENS = ['0', 'false', 'null', 'undefined', 'none', 'n/a', 'na', 'no', 'n', 'off', 'mock', 'placeholder']

function normalizeBody(value) {
  if (Array.isArray(value)) return { list: value, dataList: value }
  return value && typeof value === 'object' ? value : {}
}

function hasEnvelopeDataShape(value, includeEmptyArrays = true) {
  const body = normalizeBody(value)
  if (!Object.keys(body).length) return false
  return hasReadableLotteryShape(body, includeEmptyArrays)
}

function firstEnvelopeBody(source) {
  if (Array.isArray(source)) return source
  if (!source || typeof source !== 'object') return null
  let firstNonEmpty = null
  for (const key of ENVELOPE_KEYS) {
    const value = source[key]
    if (Array.isArray(value)) return value
    if (value && typeof value === 'object' && Object.keys(value).length) {
      if (!firstNonEmpty) firstNonEmpty = value
      if (hasEnvelopeDataShape(value, false)) return value
    }
  }
  return firstNonEmpty || source
}

function unwrapEnvelopeBody(value, depth = 0) {
  const body = normalizeBody(value)
  if (!Object.keys(body).length || depth >= 3) return body
  for (const key of ENVELOPE_KEYS) {
    const nested = body[key]
    if (!nested || (typeof nested !== 'object' && !Array.isArray(nested))) continue
    const unwrapped = unwrapEnvelopeBody(nested, depth + 1)
    if (!Object.keys(unwrapped).length) continue
    const parent = { ...body }
    delete parent.content
    delete parent.extra
    delete parent.page
    delete parent.data
    return {
      ...parent,
      ...unwrapped
    }
  }
  return body
}

function collectEnvelopeBodies(source, depth = 0, out = [], scanDiagnosticTree = false) {
  const body = normalizeBody(source)
  if (Object.keys(body).length) out.push(body)
  if (!source || typeof source !== 'object' || depth >= MAX_ENVELOPE_DEPTH) return out
  if (Array.isArray(source)) {
    source.forEach(item => collectEnvelopeBodies(item, depth + 1, out, scanDiagnosticTree))
    return out
  }
  const keys = scanDiagnosticTree
    ? Object.keys(source)
    : ENVELOPE_KEYS.concat(DIAGNOSTIC_ENVELOPE_KEYS)
  for (const key of keys) {
    const value = source[key]
    if (!value || (typeof value !== 'object' && !Array.isArray(value))) continue
    collectEnvelopeBodies(
      value,
      depth + 1,
      out,
      scanDiagnosticTree || DEEP_DIAGNOSTIC_ENVELOPE_KEYS.includes(key)
    )
  }
  return out
}

function responseToken(value) {
  return String(value == null ? '' : value).trim().toLowerCase()
}

function isTechnicalFailureMessage(value) {
  const message = String(value == null ? '' : value).trim()
  if (!message) return false
  const token = message.toLowerCase()
  return (
    token === 'network error' ||
    token === 'request failed' ||
    token.includes('request failed with status code') ||
    token.includes('timeout') ||
    /\beconn[a-z_]*\b/i.test(message) ||
    /\b(typeerror|referenceerror|syntaxerror|rangeerror|urierror)\b/i.test(message) ||
    /<\/?[a-z][\s\S]*>/i.test(message) ||
    /\n\s*at\s+.+\(.+:\d+:\d+\)/.test(message) ||
    /\bhttps?:\/\/\S+/i.test(message)
  )
}

function isOptimisticSuccessMessage(value) {
  const token = responseToken(value).replace(/[.!。！]+$/g, '')
  if (!token) return false
  const hasSuccessCopy = [
    'ok',
    'success',
    'successful',
    'succeed',
    'succeeded',
    'done',
    'saved',
    'complete',
    'completed',
    'sucesso',
    'exito',
    'éxito',
    '操作成功',
    '成功'
  ].includes(token) ||
    /\b(?:ok|success|successful|succeed|succeeded|done|saved|complete|completed)\b/.test(token) ||
    token.includes('sucesso') ||
    token.includes('exito') ||
    token.includes('éxito') ||
    token.includes('成功')
  const hasFailureContext = isNeedLoginToken(token) ||
    isBlockingToken(token) ||
    hasFailurePhrase(token) ||
    token.includes('阻止') ||
    token.includes('失败') ||
    token.includes('失敗') ||
    token.includes('不可用') ||
    token.includes('未接入') ||
    token.includes('只读') ||
    token.includes('只讀') ||
    token.includes('fake')
  return hasSuccessCopy && !hasFailureContext
}

function safeLotteryUserMessage(value) {
  if (!value) return ''
  return isTechnicalFailureMessage(value) || isOptimisticSuccessMessage(value) ? '' : value
}

function isOkToken(value) {
  const token = responseToken(value)
  return token === 'ok' || token === 'success' || token === '0' || token === '200'
}

function isNeedLoginToken(value) {
  const token = responseToken(value)
  return Boolean(token) && (
    [
      '401',
      'unauthorized',
      'need_login',
      'login_required',
      'token_expired',
      'expired_token',
      'login_expired',
      'session_expired'
    ].includes(token) ||
    /(^|[^\d])401([^\d]|$)/.test(token) ||
    token.includes('unauthorized') ||
    token.includes('need_login') ||
    token.includes('login_required') ||
    token.includes('token_expired') ||
    token.includes('expired_token') ||
    token.includes('login_expired') ||
    token.includes('session_expired')
  )
}

function isNegativeFlag(value) {
  const token = responseToken(value)
  return value === false || value === 0 || ['false', '0', 'no', 'n', 'off'].includes(token)
}

function isPositiveProofFlag(value) {
  const token = responseToken(value)
  return value === true || value === 1 || ['true', '1', 'yes', 'y', 'on', 'ok', 'success'].includes(token)
}

function hasMeaningfulProofValue(value) {
  if (isPositiveProofFlag(value)) return true
  if (Array.isArray(value)) return value.length > 0 && value.every(hasMeaningfulProofValue)
  if (value && typeof value === 'object') return Object.keys(value).length > 0 && !hasBlockingMarker(value)
  const text = String(value == null ? '' : value).trim()
  const token = responseToken(text)
  return Boolean(text) &&
    !FALSEY_PROOF_TOKENS.includes(token) &&
    !isBlockingToken(text) &&
    !hasFailurePhrase(text)
}

function hasMeaningfulWriteCountProofValue(value) {
  if (typeof value === 'number') return Number.isInteger(value) && value > 0
  if (typeof value !== 'string') return false
  const text = value.trim()
  if (!text) return false
  return /^\d+$/.test(text) && /[1-9]/.test(text)
}

function isBlockingToken(value) {
  const token = responseToken(value)
  return Boolean(token) && (
    [
      'stub',
      'mock',
      'mock_mode',
      'mock-mode',
      'mock mode',
      'fallback',
      'fallback_only',
      'fallback-only',
      'fallback only',
      'mock_fallback',
      'mock-fallback',
      'mock fallback',
      'fail',
      'failed',
      'error',
      'rejected',
      'unavailable',
      'disabled',
      'read_only',
      'read_only_query',
      'read-only-query',
      'read only query',
      'read_only_mode',
      'read-only-mode',
      'read only mode',
      'readonly',
      'readonlymode',
      'readonlyquery',
      'read only',
      'read-only',
      'unimplemented',
      'not_implemented',
      'not implemented',
      'not_supported',
      'not-supported',
      'not supported',
      'not_connected',
      'not-connected',
      'not connected',
      'read_unavailable',
      'read-unavailable',
      'read unavailable',
      'readunavailable',
      'technical_marker',
      'technical-marker',
      'technical marker',
      'technicalmarker',
      'technical_error',
      'technical-error',
      'technical error',
      'technicalerror',
      'technical_exception',
      'technical-exception',
      'technical exception',
      'technicalexception',
      '技术错误',
      '技术异常',
      'read_blocked',
      'read-blocked',
      'read blocked',
      'readblocked',
      'read_failure',
      'read-failure',
      'read failure',
      'read_failed',
      'read-failed',
      'read failed',
      'unsupported',
      'only_query',
      'only-query',
      'only query',
      'query_only',
      'query-only',
      'query only',
      'blocked',
      'block',
      'route_unavailable',
      'route-unavailable',
      'route unavailable',
      'routeunavailable',
      'fail_closed',
      'fail-closed',
      'fail closed',
      'failclosed',
      'payment_backend_blocked',
      'paymentbackendblocked',
      'payment backend blocked',
      'payment-backend-blocked',
      'backend_failure',
      'backendfailure',
      'backend failure',
      'backend-failure',
      'payment_status_unconfirmed',
      'paymentstatusunconfirmed',
      'payment status unconfirmed',
      'payment-status-unconfirmed',
      'false_proof',
      'falseproof',
      'false proof',
      'false-proof',
      'writeproof:false',
      'write_proof:false',
      'write proof false',
      'write-proof false',
      'write_proof false',
      'write-proof:false',
      'action_blocked',
      'actionblocked',
      'action blocked',
      'action-blocked',
      'write_block',
      'writeblock',
      'write block',
      'write-block',
      'write_blocked',
      'writeblocked',
      'write blocked',
      'write-blocked',
      'data_unavailable',
      'dataunavailable',
      'data unavailable',
      'data-unavailable',
      'need_login'
    ].includes(token) ||
    token.includes('read_only') ||
    token.includes('read_only_query') ||
    token.includes('read_only_mode') ||
    token.includes('read only query') ||
    token.includes('read only mode') ||
    token.includes('read-only-query') ||
    token.includes('read-only-mode') ||
    token.includes('readonlymode') ||
    token.includes('readonlyquery') ||
    token.includes('mock') ||
    token.includes('fallback') ||
    token.includes('read only') ||
    token.includes('read-only') ||
    token.includes('unimplemented') ||
    token.includes('not_implemented') ||
    token.includes('not implemented') ||
    token.includes('not_supported') ||
    token.includes('not-supported') ||
    token.includes('not supported') ||
    token.includes('not_connected') ||
    token.includes('not-connected') ||
    token.includes('not connected') ||
    token.includes('unavailable') ||
    token.includes('read_unavailable') ||
    token.includes('read-unavailable') ||
    token.includes('read unavailable') ||
    token.includes('readunavailable') ||
    token.includes('technical_marker') ||
    token.includes('technical-marker') ||
    token.includes('technical marker') ||
    token.includes('technicalmarker') ||
    token.includes('technical_error') ||
    token.includes('technical-error') ||
    token.includes('technical error') ||
    token.includes('technicalerror') ||
    token.includes('technical_exception') ||
    token.includes('technical-exception') ||
    token.includes('technical exception') ||
    token.includes('technicalexception') ||
    token.includes('技术错误') ||
    token.includes('技术异常') ||
    token.includes('read_blocked') ||
    token.includes('read-blocked') ||
    token.includes('read blocked') ||
    token.includes('readblocked') ||
    token.includes('read_failure') ||
    token.includes('read-failure') ||
    token.includes('read failure') ||
    token.includes('read_failed') ||
    token.includes('read-failed') ||
    token.includes('read failed') ||
    token.includes('unsupported') ||
    token.includes('only_query') ||
    token.includes('only-query') ||
    token.includes('only query') ||
    token.includes('query_only') ||
    token.includes('query-only') ||
    token.includes('query only') ||
    token.includes('route_unavailable') ||
    token.includes('route-unavailable') ||
    token.includes('route unavailable') ||
    token.includes('routeunavailable') ||
    token.includes('fail_closed') ||
    token.includes('fail-closed') ||
    token.includes('fail closed') ||
    token.includes('failclosed') ||
    token.includes('action_blocked') ||
    token.includes('actionblocked') ||
    token.includes('action blocked') ||
    token.includes('action-blocked') ||
    token.includes('backend_blocked') ||
    token.includes('backendblocked') ||
    token.includes('backend blocked') ||
    token.includes('backend-blocked') ||
    token.includes('backend_failure') ||
    token.includes('backendfailure') ||
    token.includes('backend failure') ||
    token.includes('backend-failure') ||
    token.includes('payment_backend_blocked') ||
    token.includes('paymentbackendblocked') ||
    token.includes('payment backend blocked') ||
    token.includes('payment-backend-blocked') ||
    token.includes('false_proof') ||
    token.includes('falseproof') ||
    token.includes('false proof') ||
    token.includes('false-proof') ||
    token.includes('writeproof:false') ||
    token.includes('write_proof:false') ||
    token.includes('write proof false') ||
    token.includes('write-proof false') ||
    token.includes('write_proof false') ||
    token.includes('write-proof:false') ||
    token.includes('payment_status_unconfirmed') ||
    token.includes('paymentstatusunconfirmed') ||
    token.includes('payment status unconfirmed') ||
    token.includes('payment-status-unconfirmed') ||
    token.includes('write_block') ||
    token.includes('writeblock') ||
    token.includes('write block') ||
    token.includes('write-block') ||
    token.includes('write_blocked') ||
    token.includes('writeblocked') ||
    token.includes('write blocked') ||
    token.includes('write-blocked') ||
    token.includes('data_unavailable') ||
    token.includes('dataunavailable') ||
    token.includes('data unavailable') ||
    token.includes('data-unavailable') ||
    token.includes('need_login')
  )
}

function isQueryOnlyMessage(value) {
  const token = responseToken(value)
  return Boolean(token) && (
    token.includes('只支持查询') ||
    token.includes('仅支持查询') ||
    token.includes('只可查询') ||
    token.includes('query only') ||
    token.includes('query-only') ||
    token.includes('only query') ||
    token.includes('only supports query') ||
    token.includes('only support query')
  )
}

function isMetadataOnlySoftMessage(value) {
  const token = responseToken(value)
  return Boolean(token) && (
    token.includes('metadata-only') ||
    token.includes('metadata only')
  )
}

function hasFailurePhrase(value) {
  const token = responseToken(value)
  return Boolean(token) && (
    ['fail', 'failed', 'failure', 'error'].includes(token) ||
    /\b(?:bet|write|request|operation|action|backend|route|api|server)\s+(?:failed|fail|failure|error)\b/.test(token) ||
    /\b(?:failed|fail|failure|error)\s+(?:bet|write|request|operation|action|backend|route|api|server)\b/.test(token)
  )
}

function isBlockingMessage(value) {
  if (isTechnicalFailureMessage(value)) return false
  const token = responseToken(value)
  return Boolean(token) && (
    isBlockingToken(value) ||
    token.includes('mock') ||
    token.includes('stub') ||
    hasFailurePhrase(value) ||
    token.includes('not supported') ||
    token.includes('not_supported') ||
    token.includes('未接入') ||
    token.includes('只读') ||
    isQueryOnlyMessage(value) ||
    token.includes('阻止假成功') ||
    token.includes('失败')
  )
}

function isPendingWriteToken(value) {
  const token = responseToken(value)
  return Boolean(token) && (
    PENDING_WRITE_TOKENS.includes(token) ||
    token.includes('pending') ||
    token.includes('waiting') ||
    token.includes('processing') ||
    token.includes('in_progress') ||
    token.includes('in-progress') ||
    token.includes('in progress')
  )
}

function isUnconfirmedPaymentStatus(value) {
  const token = responseToken(value)
  return token === 'unconfirmed' ||
    token === 'not_confirmed' ||
    token === 'not-confirmed' ||
    token === 'not confirmed' ||
    token === 'status_unconfirmed' ||
    token === 'status-unconfirmed' ||
    token === 'status unconfirmed' ||
    token === 'payment_status_unconfirmed' ||
    token === 'payment-status-unconfirmed' ||
    token === 'payment status unconfirmed' ||
    token === 'paymentstatusunconfirmed'
}

function isActiveBlockingFlag(value) {
  const token = responseToken(value)
  return value === true ||
    value === 1 ||
    ['true', '1', 'yes', 'y', 'on'].includes(token) ||
    isBlockingToken(value)
}

function isActiveNeedLoginFlag(value) {
  return isActiveBlockingFlag(value) || isNeedLoginToken(value)
}

function hasNegativeProofMarker(body) {
  return body && typeof body === 'object' && PROOF_KEYS.some(key => isNegativeFlag(body[key]))
}

function hasInvalidProofMarker(body) {
  return body && typeof body === 'object' && (
    EXPLICIT_PROOF_KEYS.some(key =>
      body[key] !== undefined && body[key] !== null && !hasMeaningfulProofValue(body[key])
    ) ||
    WRITE_COUNT_PROOF_KEYS.some(key =>
      body[key] !== undefined && body[key] !== null && !hasMeaningfulWriteCountProofValue(body[key])
    )
  )
}

function hasBlockingMarker(body) {
  if (!body || typeof body !== 'object') return false
  if (body.success === false || responseToken(body.success) === 'false') return true
  if (hasInvalidProofMarker(body)) return true
  if (BLOCKING_FLAG_KEYS.some(key => isActiveBlockingFlag(body[key]))) return true
  if (PAYMENT_STATUS_KEYS.some(key => isUnconfirmedPaymentStatus(body[key]))) return true
  if (MESSAGE_KEYS.some(key => isBlockingMessage(body[key]))) return true
  return MARKER_KEYS.some(key => isBlockingToken(body[key]))
}

function hasPendingWriteMarker(body) {
  if (!body || typeof body !== 'object') return false
  return [
    body.status,
    body.state,
    body.result,
    body.code,
    body.errorCode,
    body.launchStatus,
    body.forwardStatus,
    body.writeStatus,
    body.write_status,
    body.betStatus,
    body.bet_status,
    body.cancelStatus,
    body.cancel_status
  ].some(isPendingWriteToken) || MESSAGE_KEYS.some(key => isPendingWriteToken(body[key]))
}

function hasBackendUnavailableMarker(body) {
  return body && typeof body === 'object' && [
    body.notConnected,
    body.not_connected,
    body.readFailure,
    body.read_failure,
    body.readFailed,
    body.read_failed,
    body.readUnavailable,
    body.read_unavailable,
    body.routeUnavailable,
    body.route_unavailable,
    body.backendBlocked,
    body.backend_blocked,
    body.backendFailure,
    body.backend_failure,
    body.writeBlocked,
    body.write_blocked,
    body.technicalMarker,
    body.technical_marker,
    body.technicalError,
    body.technical_error,
    body.technicalException,
    body.technical_exception
  ].some(isActiveBlockingFlag)
}

function hasReadableLotteryContent(body) {
  if (!body || typeof body !== 'object') return false
  if (body.readOnlyQuery === true) return true
  return hasReadableLotteryShape(body)
}

function hasReadableLotteryShape(body, includeEmptyArrays = true) {
  if (!body || typeof body !== 'object') return false
  if ([
    'list',
    'records',
    'dataList',
    'historyLotteryInfoList',
    'betRecordList',
    'orderList',
    'items',
    'pageList',
    'allLotteryList',
    'lotteryList',
    'playRuleList',
    'playList'
  ].some(key => {
    const value = body[key]
    return Array.isArray(value) ? includeEmptyArrays || value.length > 0 : Boolean(value)
  })) return true
  if ([
    'balance',
    'totalBalance',
    '_balUsable',
    'balUsable',
    'dateStr',
    'now',
    'time',
    'currIssueNo',
    'issueNo',
    'currBetEndTime',
    'betEndTime',
    'currLotteryTime',
    'endTime',
    'lastIssueNo',
    'lastLotteryResult',
    'todayBetCurrAmount',
    'todayBetEffAmount',
    'todayBonusAmount',
    'todayProfitLossAmount'
  ].some(key => body[key] !== undefined && body[key] !== null && body[key] !== '')) return true
  return ['detail', 'order', 'record'].some(key => {
    const value = body[key]
    return value && typeof value === 'object' && Object.keys(value).length > 0
  })
}

function hasReadBlockingMarker(body, bodies = []) {
  if (!body || typeof body !== 'object') return false
  if (body.success === false || responseToken(body.success) === 'false') return true
  const hasReadableContent = bodies.some(hasReadableLotteryContent)
  const bodyHasReadableContent = hasReadableLotteryContent(body)
  if (MESSAGE_KEYS.some(key =>
    isBlockingMessage(body[key]) &&
    !(
      hasReadableContent &&
      (isQueryOnlyMessage(body[key]) || (!bodyHasReadableContent && isMetadataOnlySoftMessage(body[key])))
    )
  )) return true
  if (isActiveBlockingFlag(body.dataUnavailable) || isActiveBlockingFlag(body.data_unavailable)) {
    return true
  }
  if (BLOCKING_FLAG_KEYS.some(key => {
    const isBlockingValue = isActiveBlockingFlag(body[key])
    if (!isBlockingValue) return false
    return !READ_SAFE_FLAG_KEYS.includes(key) || !hasReadableContent
  })) return true
  if (PAYMENT_STATUS_KEYS.some(key => isUnconfirmedPaymentStatus(body[key]))) return true
  return MARKER_KEYS.some(key => isBlockingToken(body[key]))
}

function hasOkMarker(body) {
  if (!body || typeof body !== 'object') return false
  return body.success === true ||
    isOkToken(body.status) ||
    isOkToken(body.state) ||
    isOkToken(body.result) ||
    isOkToken(body.code) ||
    isOkToken(body.errorCode)
}

function hasWriteLikeResult(body) {
  if (!body || typeof body !== 'object') return false
  return [
    body.orderNo,
    body.betOrderNo,
    body.mainOrderNo,
    body.orderId,
    body.recordNo,
    body.cancelOrderNo,
    body.cancel_order_no,
    body.refundOrderNo,
    body.refund_order_no,
    body.cancelled,
    body.canceled,
    body.cancelSuccess,
    body.cancel_success,
    body.refunded,
    body.refundSuccess,
    body.refund_success
  ].some(value => value !== undefined && value !== null && String(value).trim() !== '')
}

function hasLotteryDomainProof(body) {
  return body && typeof body === 'object' && LOTTERY_DOMAIN_PROOF_KEYS.some(key => hasMeaningfulProofValue(body[key]))
}

function hasGenericLotteryWriteProof(body) {
  return body && typeof body === 'object' && GENERIC_LOTTERY_WRITE_PROOF_KEYS.some(key => hasMeaningfulProofValue(body[key]))
}

function hasWriteProofField(body) {
  return body && typeof body === 'object' && PROOF_KEYS.some(key => body[key] !== undefined && body[key] !== null)
}

function hasUnscopedGenericLotteryWriteProof(bodies) {
  const hasDomainProof = bodies.some(hasLotteryDomainProof)
  return !hasDomainProof && bodies.some(hasGenericLotteryWriteProof)
}

function hasMetadataOnlyReadMessage(body) {
  return body && typeof body === 'object' && MESSAGE_KEYS.some(key => isMetadataOnlySoftMessage(body[key]))
}

function isMetadataOnlyReadableOkResponse(bodies) {
  return bodies.some(hasOkMarker) &&
    bodies.some(hasReadableLotteryContent) &&
    bodies.some(hasMetadataOnlyReadMessage) &&
    !bodies.some(hasWriteLikeResult) &&
    !bodies.some(hasWriteProofField) &&
    !bodies.some(hasPendingWriteMarker) &&
    !bodies.some(body => hasReadBlockingMarker(body, bodies))
}

export function lotteryResponseBody(response) {
  if (!response) return {}
  const body = firstEnvelopeBody(response)
  return unwrapEnvelopeBody(body)
}

export function isLotteryNeedLoginResponse(response) {
  return collectEnvelopeBodies(response).some(body =>
    MARKER_KEYS.some(key => isNeedLoginToken(body[key])) ||
    NEED_LOGIN_FLAG_KEYS.some(key => isActiveNeedLoginFlag(body[key])) ||
    MESSAGE_KEYS.some(key => isNeedLoginToken(body[key]))
  )
}

export function isLotteryOkResponse(response) {
  const bodies = collectEnvelopeBodies(response)
  if (!bodies.length) return false
  if (bodies.some(hasBlockingMarker)) return isMetadataOnlyReadableOkResponse(bodies)
  if (bodies.some(hasPendingWriteMarker)) return false
  if (hasUnscopedGenericLotteryWriteProof(bodies)) return false
  return bodies.some(hasOkMarker)
}

export function hasLotteryWriteProof(response) {
  const bodies = collectEnvelopeBodies(response)
  if (!bodies.length) return false
  if (bodies.some(hasBlockingMarker)) return false
  if (bodies.some(hasPendingWriteMarker)) return false
  const hasWriteContext = bodies.some(hasWriteLikeResult)
  return bodies.some(body => {
    if (!body || typeof body !== 'object') return false
    if (hasLotteryDomainProof(body)) return true
    return hasWriteContext && WRITE_COUNT_PROOF_KEYS.some(key => hasMeaningfulWriteCountProofValue(body[key]))
  })
}

export function isLotteryReadableResponse(response) {
  const bodies = collectEnvelopeBodies(response)
  if (!bodies.length) return false
  if (bodies.some(hasNegativeProofMarker)) return false
  if (bodies.some(body => hasReadBlockingMarker(body, bodies))) return false
  return bodies.some(hasOkMarker) && bodies.some(hasReadableLotteryContent)
}

export function isLotteryReadOnlyQueryResponse(response) {
  return collectEnvelopeBodies(response).some(body =>
    body && typeof body === 'object' && (body.readOnlyQuery === true || body.read_only_query === true)
  )
}

export function isLotteryWriteBlockedResponse(response) {
  const bodies = collectEnvelopeBodies(response)
  if (!bodies.length) return false
  return bodies.some(hasBlockingMarker) || bodies.some(hasPendingWriteMarker)
}

export function lotteryResponseMessage(response, fallback = '') {
  const bodies = collectEnvelopeBodies(response)
  for (const body of bodies) {
    const message = firstValue(...MESSAGE_KEYS.map(key => body[key]))
    const safeMessage = safeLotteryUserMessage(message)
    if (safeMessage) return safeMessage
  }
  if (bodies.some(hasBackendUnavailableMarker)) {
    return 'This feature is temporarily unavailable. Please try again later.'
  }
  if (bodies.some(hasNegativeProofMarker)) {
    return 'This feature is temporarily unavailable. Please try again later.'
  }
  if (bodies.some(hasOkMarker) && bodies.some(hasWriteLikeResult) && !hasLotteryWriteProof(response)) {
    return 'This feature is temporarily unavailable. Please try again later.'
  }
  if (bodies.some(hasPendingWriteMarker)) {
    return 'This feature is temporarily unavailable. Please try again later.'
  }
  if (bodies.some(hasBlockingMarker)) {
    return 'This feature is temporarily unavailable. Please try again later.'
  }
  return safeLotteryUserMessage(fallback) || DEFAULT_LOTTERY_FAILURE_MESSAGE
}

export function extractLotteryOrderNo(response) {
  const body = lotteryResponseBody(response)
  return firstValue(
    body.orderNo,
    body.betOrderNo,
    body.mainOrderNo,
    body.orderId,
    body.id,
    body.recordNo,
    body.order_no,
    body.bet_order_no,
    body.main_order_no,
    response && response.orderNo,
    response && response.betOrderNo,
    response && response.mainOrderNo,
    response && response.orderId,
    response && response.id,
    response && response.recordNo,
    response && response.order_no,
    response && response.bet_order_no,
    response && response.main_order_no
  )
}
